import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"

import { getTurnstilePublicKey } from "@/cloudflare/turnstile"
import config from "@/config"
import { ContactForm } from "@/contact/components/contact-form"
import { breadcrumbJsonLd, buildHead } from "@/core/seo"

import { Breadcrumb } from "../components/landing"

export const contactSeo = () =>
  buildHead({
    title: "Contact us",
    description:
      "Send Kestly a question, a bug report or a request about your trips, receipts, invoices or account data. We read every message and reply by email.",
    path: "/contact",
    jsonLd: [breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Contact" }])],
  })

export const Route = createFileRoute("/_brand-layout/contact")({
  head: () => {
    const { meta, links, scripts } = contactSeo()
    return { meta, links, scripts }
  },
  component: ContactRoute,
})

/** The page is prerendered, so the per-environment Turnstile key is fetched in the browser. */
function ContactRoute() {
  const { data } = useQuery({ queryKey: ["contact-page"], queryFn: () => getTurnstilePublicKey() })
  return <ContactPage cloudflareTurnstilePublicKey={data?.cloudflareTurnstilePublicKey} />
}

export function ContactPage({
  cloudflareTurnstilePublicKey,
}: {
  cloudflareTurnstilePublicKey?: string
}) {
  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Contact" }]} />
      <header className="bg-muted/40 px-band py-8 md:py-12">
        <h1 className="text-3xl font-bold tracking-tight md:text-5xl">Contact us</h1>
        <p className="text-muted-foreground mt-3 max-w-[60ch] text-lg">
          Questions about your records, a bug, or a request about your data? Write to us and we will
          reply by email. We use what you enter here only to answer you; see the{" "}
          <a
            href="/legal/privacy"
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            privacy policy
          </a>
          .
        </p>
      </header>
      <section className="px-band py-8">
        <ContactForm
          cloudflareTurnstilePublicKey={cloudflareTurnstilePublicKey}
          className="max-w-xl"
        />
        <p className="text-muted-foreground mt-6 max-w-xl">
          Prefer email? Write to{" "}
          <a
            href={`mailto:${config.core.supportEmail}`}
            className="focus-visible:ring-ring inline-flex min-h-11 items-center rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            {config.core.supportEmail}
          </a>
          . A person replies; we do not guarantee a response time. Common answers are in the{" "}
          <a
            href="/faq#app"
            className="focus-visible:ring-ring rounded-sm underline underline-offset-2 outline-none focus-visible:ring-2"
          >
            FAQ
          </a>
          .
        </p>
      </section>
    </>
  )
}
